<script>
import WorkOrderCustomerSiteTab from './WorkOrderCustomerSiteTab'
import WorkOrderDetailHeader from './WorkOrderDetailHeader'
import WorkOrderDetailTabs from './WorkOrderDetailTabs'
import WorkOrderGateSidebar from './WorkOrderGateSidebar'
import WorkOrderInstallTab from './WorkOrderInstallTab'
import WorkOrderPlaceholderTab from './WorkOrderPlaceholderTab'
import WorkOrderScheduleCrewTab from './WorkOrderScheduleCrewTab'
import * as customProfileApi from '@/services/rest-api-sms/lib/custom-profile/custom-profile'

export default {
  name: 'WorkOrderManagementDetail',
  components: {
    WorkOrderCustomerSiteTab,
    WorkOrderDetailHeader,
    WorkOrderDetailTabs,
    WorkOrderGateSidebar,
    WorkOrderInstallTab,
    WorkOrderPlaceholderTab,
    WorkOrderScheduleCrewTab,
  },
  props: {
    workOrder: {
      type: Object,
      default: null,
    },
    isUpdatingStatus: {
      type: Boolean,
      default: false,
    },
    isUpdatingSchedule: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'customer-site',
      appliedStageTabSignature: '',
      customProfiles: [],
      isLoadingCustomProfiles: false,
    }
  },
  computed: {
    woNumber() {
      return this.getValue(['wo_number', 'woNumber'], 'WO-1047')
    },
    customerName() {
      return this.getValue(['customer', 'customer_name', 'customerName'], 'Customer C')
    },
    city() {
      return this.getValue(['city', 'site_city', 'location'], 'Medan')
    },
    salesOrderNumber() {
      return this.getValue(['so_number', 'sales_order_number', 'dt_number'], 'SO-3003')
    },
    unplannedUnitsText() {
      const unplanned = this.getValue(['unplanned_units', 'units_unplanned'], 0)
      const total = this.getValue(['total_units', 'units_total'], 2)

      return `${unplanned} of ${total} units still unplanned`
    },
    subtitle() {
      return `${this.customerName} - ${this.city} - ${this.salesOrderNumber} (${this.unplannedUnitsText})`
    },
    picName() {
      return this.getValue(['pic', 'pic_name', 'picName'], '-')
    },
    picContact() {
      return this.getValue(['pic_contact', 'picContact', 'phone'], '-')
    },
    latitude() {
      return this.getValue(['latitude', 'lat'], '-')
    },
    longitude() {
      return this.getValue(['longitude', 'long', 'lng'], '-')
    },
    statusText() {
      return this.getStatusText(this.getValue(['status', 'status_name', 'work_order_status', 'work_order_plan_status'], 'To Schedule'))
    },
    currentStageIndex() {
      const normalizedStatus = this.normalizeStatusKey(this.statusText)
      const index = this.workflowStages.findIndex((stage) => this.normalizeStatusKey(stage.label) === normalizedStatus)

      return index === -1 ? -1 : index
    },
    currentStage() {
      return this.workflowStages[this.currentStageIndex] || null
    },
    previousStage() {
      if (this.currentStageIndex <= 0) return null

      return this.workflowStages[this.currentStageIndex - 1]
    },
    nextStage() {
      if (this.currentStageIndex === -1 || this.currentStageIndex >= this.workflowStages.length - 1) return null

      return this.workflowStages[this.currentStageIndex + 1]
    },
    statusLabel() {
      return this.currentStage ? `${this.currentStage.step} - ${this.currentStage.label}` : this.statusText
    },
    previousStatusLabel() {
      return this.previousStage ? `${this.previousStage.step} - ${this.previousStage.label}` : ''
    },
    nextStatusLabel() {
      return this.nextStage ? `${this.nextStage.step} - ${this.nextStage.label}` : ''
    },
    canMovePreviousStatus() {
      return Boolean(this.previousStage && !this.isUpdatingStatus)
    },
    canMoveNextStatus() {
      return Boolean(this.nextStage && this.isCurrentGateMet && !this.isUpdatingStatus)
    },
    gateTitle() {
      if (!this.currentStage || !this.nextStage) return `Gate: ${this.statusText}`

      return `Gate: ${this.currentStage.step} ${this.currentStage.label} to ${this.nextStage.step} ${this.nextStage.gateLabel || this.nextStage.label}`
    },
    gateChecklistItems() {
      const gateKey = this.normalizeStatusKey(this.statusText)
      const rules = {
        preparing: [
          { label: 'PIC name filled', passed: this.hasValue(this.picName) },
          { label: 'PIC contact filled', passed: this.hasValue(this.picContact) },
          { label: 'What to install set', passed: this.hasInstallSet },
        ],
        toschedule: [
          { label: 'Customer confirmed the date', passed: this.isPlanConfirmed },
          { label: 'Install date and time slot set', passed: this.hasValidPlanDatetime },
        ],
        scheduled: [
          { label: 'Technician filled', passed: this.hasTechnicianSet },
        ],
      }

      return rules[gateKey] || []
    },
    gateHelperText() {
      if (!this.currentStage || !this.nextStage) return 'No next stage available.'
      if (!this.gateChecklistItems.length) return `The WO can move from ${this.currentStage.label} to ${this.nextStage.gateLabel || this.nextStage.label}.`

      return `The WO stays in ${this.currentStage.label} until every item is met.`
    },
    gateActionLabel() {
      return this.nextStage ? `Move to ${this.nextStage.step} - ${this.nextStage.label}` : 'Move to next stage'
    },
    isCurrentGateMet() {
      return this.gateChecklistItems.length ? this.gateChecklistItems.every((item) => item.passed) : true
    },
    isCustomerPicComplete() {
      return this.hasValue(this.picName) && this.hasValue(this.picContact)
    },
    stageDefaultActiveTab() {
      const stageKey = this.normalizeStatusKey(this.currentStage ? this.currentStage.label : this.statusText)

      if (stageKey === 'preparing') return this.isCustomerPicComplete ? 'what-to-install' : 'customer-site'
      if (stageKey === 'toschedule' || stageKey === 'scheduled') return 'schedule-crew'

      return 'customer-site'
    },
    stageTabSignature() {
      const stageKey = this.normalizeStatusKey(this.currentStage ? this.currentStage.label : this.statusText)

      return `${stageKey}:${this.isCustomerPicComplete ? 'pic-complete' : 'pic-incomplete'}`
    },
    hasInstallSet() {
      return this.dataErp.length > 0 || this.hasTruthyValue(['install_packages', 'installPackages', 'packages', 'what_to_install', 'whatToInstall'])
    },
    hasValidPlanDatetime() {
      const date = new Date(this.planDatetime)

      return Boolean(this.planDatetime && !Number.isNaN(date.getTime()))
    },
    hasTechnicianSet() {
      const technicianQty = Number(this.techniciansNeeded)

      return this.hasTruthyValue(['technician', 'technician_name', 'technicianName', 'technician_id', 'technicianId', 'technicians', 'crew']) || (Number.isFinite(technicianQty) && technicianQty > 0)
    },
    sidebarRows() {
      return [
        { label: 'Area', value: this.getValue(['area'], 'Medan'), meta: 'Auto' },
      ]
    },
    dataErp() {
      const dataErp = this.getValue(['data_erp', 'dataErp'], [])

      return Array.isArray(dataErp) ? dataErp : []
    },
    gpsConfiguration() {
      return this.getValue(['gps_configuration', 'gpsConfiguration'], 'Generic')
    },
    selectedDeviceCode() {
      return this.getDeviceCodeFromWorkOrder()
    },

    techniciansNeeded() {
      return this.getValue(['technicians_needed', 'techniciansNeeded', 'technician_qty'], '3')
    },
    installDuration() {
      return this.getValue(['default_install_duration', 'defaultInstallDuration'], '2 h')
    },
    planDatetime() {
      return this.getValue(['plan_datetime', 'planDatetime'], '')
    },
    isPlanConfirmed() {
      return this.toBoolean(this.getValue(['is_plan_confirmed', 'isPlanConfirmed'], false))
    },
    workflowStages() {
      return [
        { step: 1, label: 'Preparing' },
        { step: 2, label: 'To Schedule' },
        { step: 3, label: 'Scheduled' },
        { step: 4, label: 'Confirmed Technician' },
        { step: 5, label: 'Install', gateLabel: 'Installing' },
        { step: 6, label: 'Completed' },
      ]
    },
  },
  watch: {
    stageTabSignature: {
      immediate: true,
      handler(signature) {
        if (signature === this.appliedStageTabSignature) return

        this.activeTab = this.stageDefaultActiveTab
        this.appliedStageTabSignature = signature
      },
    },
    selectedDeviceCode: {
      immediate: true,
      handler() {
        this.getCustomProfiles()
      },
    },
  },
  methods: {
    getValue(keys, fallback) {
      if (!this.workOrder) return fallback

      const key = keys.find((item) => this.workOrder[item] !== undefined && this.workOrder[item] !== null && this.workOrder[item] !== '')

      return key ? this.workOrder[key] : fallback
    },
    getStatusText(status) {
      if (!status) return 'To Schedule'
      if (typeof status === 'string') return status

      return status.name || status.status || status.title || status.label || status.value || 'To Schedule'
    },
    normalizeStatusKey(status) {
      return this.getStatusText(status)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
    },
    hasValue(value) {
      return value !== undefined && value !== null && value !== '' && value !== '-'
    },
    hasTruthyValue(keys) {
      if (!this.workOrder) return false

      return keys.some((key) => {
        const value = this.workOrder[key]

        if (Array.isArray(value)) return value.length > 0
        if (typeof value === 'boolean') return value
        if (typeof value === 'number') return value > 0
        if (typeof value === 'string' && ['false', '0', 'no'].includes(value.toLowerCase().trim())) return false

        return this.hasValue(value)
      })
    },
    toBoolean(value) {
      if (typeof value === 'boolean') return value
      if (typeof value === 'number') return value > 0
      if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.toLowerCase().trim())

      return Boolean(value)
    },
    getDeviceCodeFromWorkOrder() {
      if (!this.workOrder) return null

      const directDeviceCode = this.firstValue([
        this.workOrder.device_code,
        this.workOrder.deviceCode,
        this.workOrder.device?.device_code,
        this.workOrder.device?.deviceCode,
        this.workOrder.vehicle?.device?.device_code,
        this.workOrder.vehicle?.device?.deviceCode,
      ])

      if (this.hasDeviceCodeValue(directDeviceCode)) return directDeviceCode

      const dataErpDeviceCode = this.dataErp
        .map((item) => this.getDeviceCodeFromItem(item))
        .find((deviceCode) => this.hasDeviceCodeValue(deviceCode))

      return this.hasDeviceCodeValue(dataErpDeviceCode) ? dataErpDeviceCode : null
    },
    getDeviceCodeFromItem(item) {
      if (!item || typeof item !== 'object') return null

      return this.firstValue([
        item.device_code,
        item.deviceCode,
        item.device?.device_code,
        item.device?.deviceCode,
        item.vehicle?.device?.device_code,
        item.vehicle?.device?.deviceCode,
        item.vehicle_status?.vehicle?.device?.device_code,
        item.vehicle_status?.vehicle?.device?.deviceCode,
        item.vehicleStatus?.vehicle?.device?.device_code,
        item.vehicleStatus?.vehicle?.device?.deviceCode,
        item.package?.device_code,
        item.package?.deviceCode,
        item.gps?.device_code,
        item.gps?.deviceCode,
      ])
    },
    firstValue(values) {
      return values.find((value) => value !== undefined && value !== null && value !== '')
    },
    hasDeviceCodeValue(value) {
      return value !== undefined && value !== null && value !== ''
    },
    async getCustomProfiles() {
      this.isLoadingCustomProfiles = true

      try {
        const customProfiles = this.selectedDeviceCode
          ? await this.fetchCustomProfilesByDeviceCode(this.selectedDeviceCode)
          : await this.fetchAllCustomProfiles()

        this.customProfiles = customProfiles.length ? customProfiles : await this.fetchAllCustomProfiles()
      } catch (error) {
        this.customProfiles = []
        const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed loading custom profiles'
        this.$toast?.error(message)
      } finally {
        this.isLoadingCustomProfiles = false
      }
    },
    async fetchCustomProfilesByDeviceCode(deviceCode) {
      const response = await customProfileApi.get({
        page: 1,
        limit: 1000,
        filters: [`device_code eq ${deviceCode}`],
      })

      return response?.data?.paginated_result || []
    },
    async fetchAllCustomProfiles() {
      const response = await customProfileApi.get({
        page: 1,
        limit: 1000,
      })

      return response?.data?.paginated_result || []
    },
    updateGpsConfiguration(profile, installPackage) {
      if (!installPackage) return

      const customProfileId = profile ? profile.custom_profile_id || profile.id : null

      this.$emit('update-schedule', {
        gps_configurations: [
          {
            package_key: installPackage.key,
            reserved_product_ids: installPackage.reservedProductIds,
            data_erp_ids: installPackage.dataErpIds,
            custom_profile_id: customProfileId,
            gps_configuration: profile ? profile.name : 'Default',
          },
        ],
      })
    },
    updatePreviousStatus() {
      if (!this.previousStage) return

      this.$emit('update-status', this.previousStage.label)
    },
    updateNextStatus() {
      if (!this.nextStage || !this.isCurrentGateMet || this.isUpdatingStatus) return

      this.$emit('update-status', this.nextStage.label)
    },
  },
}
</script>

<template>
  <section class="wo-management-detail">
    <header class="wo-detail-hero">
      <WorkOrderDetailHeader
        :wo-number="woNumber"
        :status-label="statusLabel"
        :subtitle="subtitle"
        :previous-status-label="previousStatusLabel"
        :next-status-label="nextStatusLabel"
        :can-move-previous-status="canMovePreviousStatus"
        :can-move-next-status="canMoveNextStatus"
        :is-updating-status="isUpdatingStatus"
        @previous-status="updatePreviousStatus"
        @next-status="updateNextStatus"
      />
      <WorkOrderDetailTabs v-model="activeTab" />
    </header>

    <div class="wo-detail-grid">
      <WorkOrderCustomerSiteTab
        v-if="activeTab === 'customer-site'"
        :customer-name="customerName"
        :pic-name="picName"
        :pic-contact="picContact"
        :latitude="latitude"
        :longitude="longitude"
        :sidebar-rows="sidebarRows"
      />
      <WorkOrderInstallTab
        v-else-if="activeTab === 'what-to-install'"
        :data-erp="dataErp"
        :gps-configuration="gpsConfiguration"
        :gps-configuration-options="customProfiles"
        :loading-custom-profiles="isLoadingCustomProfiles"
        :technicians-needed="techniciansNeeded"
        :install-duration="installDuration"
        :loading="isUpdatingSchedule"
        @update-gps-configuration="updateGpsConfiguration"
        @update-technicians="$emit('update-schedule', { technician_qty: $event })"
      />
      <WorkOrderPlaceholderTab v-else-if="activeTab === 'packages'" title="Packages" />
      <WorkOrderScheduleCrewTab
        v-else
        :install-duration="installDuration"
        :city="city"
        :plan-datetime="planDatetime"
        :is-confirmed="isPlanConfirmed"
        :loading="isUpdatingSchedule"
        @update-schedule="$emit('update-schedule', $event)"
      />

      <WorkOrderGateSidebar
        :gate-title="gateTitle"
        :checklist-items="gateChecklistItems"
        :helper-text="gateHelperText"
        :action-label="gateActionLabel"
        :disabled="!isCurrentGateMet || !nextStage"
        :loading="isUpdatingStatus"
        @move-next="updateNextStatus"
      />
    </div>
  </section>
</template>

<style>
.wo-management-detail {
  min-height: 100%;
  color: #344054;
}

.wo-detail-hero {
  overflow: hidden;
  margin-bottom: 24px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.wo-detail-hero__content {
  padding: 24px 28px 0;
}

.wo-detail-hero__breadcrumb {
  margin-bottom: 14px;
  color: #667085;
  font-size: 13px;
  font-weight: 600;
}

.wo-detail-hero__main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.wo-detail-hero__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.wo-detail-hero h1 {
  margin: 0;
  color: #111827;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.wo-detail-hero p {
  margin: 0 0 24px;
  color: #667085;
  font-size: 15px;
}

.wo-stage-pill,
.wo-source-badge,
.wo-tab-badge,
.wo-skill-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  font-weight: 700;
}

.wo-stage-pill {
  padding: 7px 12px;
  border-color: #d0d5dd;
  color: #556ee6;
  background: rgba(85, 110, 230, .08);
  font-size: 13px;
}

.wo-detail-hero__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.wo-action-button {
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  color: #344054;
  background: #fff;
  font-weight: 700;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.wo-action-button--danger {
  border-color: #f46a6a;
  color: #f46a6a;
}

.wo-action-button:hover {
  opacity: 0.75;
  transform: translateY(-1px);
}

.wo-action-button:disabled {
  cursor: not-allowed;
  opacity: .65;
  transform: none;
}

.wo-detail-tabs {
  display: flex;
  gap: 26px;
  padding: 0 28px;
  border-top: 1px solid #eef2f6;
}

.wo-detail-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  border: 0;
  color: #667085;
  background: transparent;
  font-weight: 700;
  opacity: 0.72;
}

.wo-detail-tab--active {
  color: #556ee6;
  opacity: 1;
}

.wo-detail-tab--active::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 999px 999px 0 0;
  border-bottom: 3px solid #556ee6;
  content: '';
}

.wo-tab-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  color: #667085;
  background: #f9fafb;
  font-size: 12px;
}

.wo-detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  gap: 24px;
}

.wo-panel {
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.wo-customer-panel,
.wo-install-panel,
.wo-placeholder-panel,
.wo-schedule-panel {
  padding: 24px;
}

.wo-panel h2 {
  margin: 0 0 18px;
  color: #111827;
  font-size: 20px;
  font-weight: 800;
}

.wo-panel__header p {
  margin: -8px 0 22px;
  color: #667085;
  line-height: 1.6;
}

.wo-input-grid,
.wo-schedule-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}

.wo-field-card,
.wo-install-field,
.wo-schedule-field,
.wo-package-card,
.wo-map-placeholder,
.wo-summary-row,
.wo-checklist__item,
.wo-schedule-extra {
  border: 1px solid #eef2f6;
  border-radius: 8px;
  background: #fff;
}

.wo-field-card {
  min-height: 108px;
  padding: 16px;
}

.wo-field-card__header,
.wo-schedule-field label,
.wo-install-field--select label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  color: #667085;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.wo-source-badge,
.wo-skill-badge {
  padding: 4px 8px;
  border-color: #d0d5dd;
  color: #556ee6;
  background: rgba(85, 110, 230, .08);
  font-size: 11px;
  letter-spacing: 0;
  white-space: nowrap;
}

.wo-field-card__value,
.wo-select-field,
.wo-install-field--select div {
  color: #111827;
  font-size: 16px;
  font-weight: 800;
}

.wo-site-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(260px, 0.95fr);
  gap: 18px;
}

.wo-map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 190px;
  padding: 18px;
  border-style: dashed;
  text-align: left;
}

.wo-map-placeholder__pin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  color: #556ee6;
  background: rgba(85, 110, 230, .08);
  font-size: 24px;
}

.wo-map-placeholder__title {
  color: #111827;
  font-weight: 800;
}

.wo-map-placeholder__subtitle {
  margin-top: 4px;
  color: #667085;
  font-size: 13px;
}

.wo-site-summary {
  display: grid;
  gap: 10px;
}

.wo-summary-row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
}

.wo-summary-row span,
.wo-summary-row strong,
.wo-summary-row em {
  font-weight: 700;
}

.wo-summary-row strong {
  color: #111827;
  font-weight: 800;
}

.wo-summary-row em {
  color: #667085;
  font-size: 12px;
  font-style: normal;
}

.wo-placeholder-panel p {
  margin: 0;
}

.wo-package-card {
  padding: 20px;
}

.wo-package-card h3 {
  margin: 0 0 18px;
  font-size: 17px;
  font-weight: 800;
}

.wo-install-grid {
  display: grid;
  grid-template-columns: minmax(180px, 0.85fr) minmax(260px, 1.15fr);
  gap: 16px;
  margin-bottom: 18px;
}

.wo-install-field {
  min-height: 76px;
  padding: 14px 16px;
}

.wo-install-field--split,
.wo-install-field--select div,
.wo-select-field,
.wo-schedule-extra,
.wo-checkbox-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.wo-install-field--split strong {
  font-size: 24px;
  font-weight: 800;
}

.wo-install-field--split span,
.wo-install-meta {
  color: #667085;
  font-weight: 700;
}

.wo-install-field--select i,
.wo-select-field i {
  font-size: 20px;
}

.wo-install-helper,
.wo-gate-panel__helper,
.wo-schedule-extra p {
  margin: 0;
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
}

.wo-install-helper {
  margin-bottom: 18px;
}

.wo-install-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #eef2f6;
  color: #667085;
  font-size: 14px;
}

.wo-skill-badge {
  min-height: 26px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 800;
}

.wo-schedule-field {
  padding: 16px;
}

.wo-select-field {
  width: 100%;
  min-height: 44px;
  padding: 0;
  border: 0;
  text-align: left;
}

.wo-schedule-field--checkbox {
  display: flex;
  flex-direction: column;
}

.wo-checkbox-row {
  justify-content: flex-start;
  margin: auto 0 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
}

.wo-checkbox-row input {
  width: 18px;
  height: 18px;
  margin: 0;
}

.wo-schedule-extra {
  display: flex;
  flex-wrap: wrap;
  padding: 16px;
}

.wo-schedule-extra p {
  flex: 1 1 260px;
}

.wo-schedule-extra__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.wo-gate-panel {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 22px;
}

.wo-gate-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.wo-gate-panel__header h2 {
  margin-bottom: 0;
  font-size: 18px;
  line-height: 1.35;
}

.wo-gate-panel__header span {
  color: #556ee6;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.wo-checklist {
  display: grid;
  gap: 12px;
  margin-bottom: 18px;
}

.wo-checklist__item {
  display: flex;
  gap: 12px;
  padding: 14px;
}

.wo-checklist__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  color: #667085;
  background: #f9fafb;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

.wo-checklist__item strong {
  display: block;
  color: #111827;
  font-weight: 800;
}

.wo-gate-panel__helper {
  margin-bottom: 24px;
}

.wo-checklist__item--met .wo-checklist__icon {
  color: #0f9f6e;
}

.wo-start-button {
  width: 100%;
  min-height: 46px;
  margin-top: auto;
  border: 1px solid #556ee6;
  border-radius: 8px;
  color: #fff;
  background: #556ee6;
  font-weight: 800;
  cursor: pointer;
}

.wo-start-button:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

@media (max-width: 1199.98px) {
  .wo-detail-hero__main,
  .wo-detail-grid,
  .wo-site-grid,
  .wo-install-grid {
    grid-template-columns: 1fr;
  }

  .wo-detail-hero__main {
    display: grid;
  }

  .wo-detail-hero__actions {
    justify-content: flex-start;
    margin-bottom: 24px;
  }
}

@media (max-width: 767.98px) {
  .wo-management-detail {
    padding: 16px;
  }

  .wo-detail-hero__content,
  .wo-detail-tabs {
    padding-right: 18px;
    padding-left: 18px;
  }

  .wo-detail-tabs {
    overflow-x: auto;
    gap: 18px;
  }

  .wo-detail-tab {
    flex: 0 0 auto;
  }

  .wo-customer-panel,
  .wo-install-panel,
  .wo-placeholder-panel,
  .wo-package-card,
  .wo-gate-panel,
  .wo-schedule-panel {
    padding: 18px;
  }

  .wo-input-grid,
  .wo-schedule-grid {
    grid-template-columns: 1fr;
  }

  .wo-summary-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .wo-install-field--split,
  .wo-schedule-extra {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }
}
</style>
